import { Properties } from '../behaviors/properties';
import type { SceneObject } from '../core/SceneObject';
import type { SceneObjectData } from '../scenedata/sceneobjectdata';
import type { PropertyDeclaration, PropertySheet, StringDeclaration } from '../scenedata/behaviors/propertiesdata';
import { isLocalAssetPath, isSiteAbsolutePath } from '../utils/assetPaths';
import { isAbsoluteHttpUrl } from './manifestValidator';
import { isOffendingAssetValue } from './validateSrcRefAssets';

/** Snapshot only sheets referenced by records this export actually carries. */
export function collectExportPropertySheets(
    records: readonly SceneObjectData[],
    liveObjectFor: (record: SceneObjectData) => SceneObject | undefined,
): Map<string, PropertySheet> {
    const sheets = new Map<string, PropertySheet>();
    for (const record of records) {
        const properties = liveObjectFor(record)?.getBehavior(Properties);
        if (!properties) continue;
        for (const behavior of record.behaviors ?? []) {
            if (behavior['@type'] !== 'Properties') continue;
            const urls = (behavior as { sheets?: string[] }).sheets;
            if (!Array.isArray(urls)) continue;
            for (const url of urls) {
                if (sheets.has(url)) continue;
                const sheet = properties.getLoadedSheet(url);
                if (sheet) sheets.set(url, structuredClone(sheet));
            }
        }
    }
    return sheets;
}

/** Sheet values use the scene's resource namespace, just like inline declarations. */
export function propertySheetAssetDeclarations(sheet: PropertySheet): StringDeclaration[] {
    return (sheet.declarations ?? []).filter((declaration): declaration is StringDeclaration =>
        declaration.type === 'string' && typeof declaration.value === 'string'
        && (isLocalAssetPath(declaration.value)
            || isAbsoluteHttpUrl(declaration.value) || isSiteAbsolutePath(declaration.value)));
}

/** Return the same snapshot when no dependency moved; never change the loaded sheet. */
export function repointPropertySheet(sheet: PropertySheet, moved: ReadonlyMap<string, string>): PropertySheet {
    const assets = new Set<PropertyDeclaration>(propertySheetAssetDeclarations(sheet));
    let changed = false;
    const declarations = sheet.declarations?.map(declaration => {
        if (!assets.has(declaration)) return declaration;
        const next = moved.get(declaration.value as string);
        if (next === undefined || next === declaration.value) return declaration;
        changed = true;
        return { ...declaration, value: next } as StringDeclaration;
    });
    return changed ? { ...sheet, declarations } : sheet;
}

/** A standalone document carries no bytes for a sheet's local dependencies. */
export function assertPropertySheetAssetsHosted(sheets: ReadonlyMap<string, PropertySheet>): void {
    for (const [url, sheet] of sheets) {
        const assets = new Set<PropertyDeclaration>(propertySheetAssetDeclarations(sheet));
        for (const declaration of sheet.declarations ?? []) {
            if (declaration.type !== 'string') continue;
            if ((typeof declaration.kind === 'string' || assets.has(declaration)) && isOffendingAssetValue(declaration.value)) {
                throw new Error(`Sheet "${url}" property "${declaration.name}" is not a hosted asset: ${declaration.value}`);
            }
        }
    }
}
